<template>
  <Form
    v-slot="{ errors }"
    ref="searchRegistryForm"
    :key="formTextKey"
    name="search-registry-form"
    role="main"
    class="search-registry-bar"
    @submit="submitSearch"
  >
    <div class="grid-x align-center" :class="{ 'registry-landing-search': isRegistryLanding }">
      <div v-if="isRegistryLanding" class="cell small-12 medium-3 large-2 xlarge-2 landing-search-label-container">
        <div :class="`landing-search-label ${isMew ? 'h5' : 'h3'}`">Find a registry</div>
      </div>
      <div :class="`search-registry-bar-input-wrapper cell small-12 ${inputWrapperClass}`">
        <div class="input-container">
          <InputText
            id="registrySearch"
            :key="inputTextKey"
            v-model="registrySearch"
            :class="{ 'is-invalid-input': !!errors['registrySearch'] }"
            type="text"
            name="registrySearch"
            :label="searchRegistryLabel"
            placeholder="Search by  name or registry ID"
            class="input-field order-number-field"
            rule="registrySearch"
            data-fs-mask
            @keyup="onRegistrySearchInput()"
          />
        </div>
      </div>
      <div
        :class="`search-registry-bar-button-wrapper cell small-12 ${
          searchBarStyle === SEARCH_BAR_STYLES.ROW || isRegistryLanding ? 'medium-3 large-2 xlarge-2' : ''
        }`"
      >
        <AtomicUiButton
          :variant="`${isRegistryLanding ? 'secondary' : 'primary'}`"
          :expanded="true"
          type="submit"
          :disabled="!!errors['registrySearch'] || registryResultsLoading"
          class="search-registry-button"
        >
          Search
        </AtomicUiButton>
      </div>
    </div>
  </Form>
</template>

<script setup>
import { veevalidate } from '@nuxt-common/util/app/utils/forms/index';
import InputText from '@nuxt-common/atomic-ui/app/components/inputText/InputText';
import { fireTag } from '@nuxt-common/util/app/utils/tagManager';
import { SEARCH_BAR_STYLES } from '~/types/searchBarTypes';
import { GET_SEARCH_REGISTRY_RESULTS } from '~/types/actions';
import { SET_REGISTRY_REDIRECT_SEARCH_TERM, SET_REGISTRY_SEARCH_OCCASION } from '~/types/mutations';
import { errorMessages, searchRegistryResult } from 'config';

import { useEnvPropsStore } from '~/stores/envProps';
import { useSearchRegistryStore } from '~/stores/searchRegistry';

const envPropsStore = useEnvPropsStore();
const searchRegistryStore = useSearchRegistryStore();

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const { Form } = veevalidate;

const props = defineProps({
  searchBarStyle: {
    type: String,
    default: SEARCH_BAR_STYLES.ROW,
    required: false,
  },
  isRegistryLanding: {
    type: Boolean,
    default: false,
    required: false,
  },
  occasion: {
    type: String,
    default: '',
    required: false,
  },
});

const route = useRoute();
const registrySearch = ref('');
const searchRegistryForm = ref('');
const inputTextKey = ref(1);
const formTextKey = ref(1);
const onRegistrySearchInput = () => {
  searchRegistryForm.value.validate();
};

const orgId = computed(() => (envPropsStore?.envProps?.entryPoint?.toUpperCase() === 'MCOM' ? 12 : 13));
const redirectSearchTerm = computed(() => searchRegistryStore.redirectSearchTerm);
const isRegistrySearchPath = computed(() => !!route.path?.includes('registry/search'));
const registryResultsLoading = computed(() => searchRegistryStore.registryResultsLoading);
const searchRegistryApiErrorResponse = computed(() => searchRegistryStore.searchRegistryApiErrorResponse);
const registryStoredSearchTerm = computed(() => searchRegistryStore.registrySearchTerm);
const registrySearchOccasion = computed(() => searchRegistryStore.registrySearchOccasion);
const isMew = computed(() => envPropsStore.envProps?.isMew);
const clearSearchInput = () => {
  registrySearch.value = '';
  inputTextKey.value += 1;
};
defineExpose({
  clearSearchInput,
});

if (redirectSearchTerm.value) {
  registrySearch.value = redirectSearchTerm.value;
}

const inputWrapperClass = computed(() => {
  if (props.isRegistryLanding) {
    return 'medium-4 large-4 xlarge-5';
  }
  if (props.searchBarStyle === SEARCH_BAR_STYLES.ROW) {
    return 'medium-7 large-7 xlarge-7';
  }
  return '';
});

const isWeddingLanding = computed(() => !!route.path.includes('/registry/wedding'));

const searchRegistryLabel = computed(() => {
  if (orgId.value === 13) {
    if (isWeddingLanding.value) {
      return searchRegistryResult.searchRegistryFullText;
    }
    return searchRegistryResult.searchRegistryText;
  }
  if (isRegistrySearchPath.value) {
    return searchRegistryResult.searchRegistryFullText;
  }
  return searchRegistryResult.searchRegistryText;
});

const submitSearch = async () => {
  registrySearch.value = registrySearch.value?.trim();
  inputTextKey.value += 1;
  formTextKey.value += 1;
  fireTag('link', {
    event_name: 'registry search',
    registry_search: registrySearch.value,
  });

  if (!isRegistrySearchPath.value) {
    searchRegistryStore[SET_REGISTRY_REDIRECT_SEARCH_TERM](registrySearch.value);
    searchRegistryStore[SET_REGISTRY_SEARCH_OCCASION](props.occasion);
    navigateTo('/registry/search');
  } else {
    await searchRegistryStore[GET_SEARCH_REGISTRY_RESULTS]({
      orgId: orgId.value,
      registrySearch: registrySearch.value,
      sortByOccasionType: registrySearchOccasion.value,
    });

    if (searchRegistryApiErrorResponse.value) {
      fireTag('link', {
        event_name: 'Registry error',
        error_category: 'registry',
        error_code: [`REG${searchRegistryApiErrorResponse.value?.response?.data?.errors?.[0]?.code || '999'}`],
        error_label: ['SEARCH_BUTTON'],
        error_message: [errorMessages.searchError],
        error_misc: [' '],
        error_scope: ['Global'],
      });
    }
  }
};

onMounted(() => {
  if (registryStoredSearchTerm.value && !registrySearch.value) {
    registrySearch.value = registryStoredSearchTerm.value;
    inputTextKey.value += 1;
  }
});
</script>

<style lang="scss" scoped>
.search-registry-bar {
  width: 100%;
}
.search-registry-bar-input-wrapper {
  :deep(.label-top) {
    margin-right: rem-calc(8);
  }
  :deep(.input-container) {
    width: 100%;
  }
}
.registry-landing-search {
  .search-registry-bar-button-wrapper {
    display: grid;
    padding-top: rem-calc(9);
    padding-left: rem-calc(24);

    @include breakpoint(small only) {
      margin-top: rem-calc(24);
      padding-top: 0;
      padding-right: rem-calc(22);
      padding-left: rem-calc(22);
    }

    .search-registry-button {
      width: rem-calc(132);

      @include breakpoint(small only) {
        width: 100%;
      }
    }
  }
  .landing-search-label-container {
    display: grid;
    justify-content: right;
    padding-top: rem-calc(16);
    padding-right: rem-calc(8);
    text-wrap: nowrap;

    @include breakpoint(small only) {
      justify-content: center;
      padding-top: 0;
      .landing-search-label {
        text-transform: uppercase;
      }
    }
  }
  .search-registry-bar-input-wrapper {
    padding-left: rem-calc(16);

    @include breakpoint(small only) {
      padding-right: rem-calc(22);
      padding-left: rem-calc(22);
    }
  }

  .input-container {
    margin-top: rem-calc(9);
    margin-bottom: 0;

    @include breakpoint(small only) {
      margin-top: rem-calc(22);
    }

    :deep(.input-container) {
      margin-top: 0;
      margin-bottom: 0;
    }
    :deep(.form-error) {
      margin-top: rem-calc(8);
    }
  }
}
</style>

<style lang="scss" brand="bcom" scoped>
.registry-landing-search {
  @include breakpoint(small only) {
    .input-container {
      margin-top: rem-calc(33);
    }
    .landing-search-label-container {
      .landing-search-label {
        font-weight: 700;
      }
    }
  }
}
</style>
